<script setup>
import { defineEmits, defineProps } from "vue";
import { useI18n } from "vue-i18n";
import { useTwoWayBinding } from '@dutypay/front-end-design-system';
import { useOneTimePasswordValidation } from "@dutypay/front-end-design-system/src/validations";


const { t } = useI18n();
const emit = defineEmits(["update", "trigger-authenticate"]);
const props = defineProps({
  modelValue: { type: Object, required: true },
});
const loginData = useTwoWayBinding(props, emit, "modelValue");

async function verifyAuthenticator() {
  loginData.value.mode = "authenticator";
  emit("trigger-authenticate");
}

const validation = {
  authenticatorOTP: [
    useOneTimePasswordValidation()
  ],
}

</script>
<script>
export default {
  name: "AuthenticatorOTPForm",
};
</script>

<template>
  <div>
    <h2>{{ t("sso.pages.LoginStages.AuthenticatorOTPForm.authenticatorOTPForm.title") }}</h2>

    <DpForm
      v-model="loginData"
      @submit="verifyAuthenticator"
      :showValidationNotification="false"
      :validation="validation"
    >

      <p>{{ t("sso.pages.LoginStages.AuthenticatorOTPForm.unlockWith.label") }}</p>

      <DpInput 
        v-model="loginData.authenticatorOTP"
        autocomplete="off"
        id="authenticatorOTP"
        :validation="validation.authenticatorOTP"
        :label="t('sso.pages.LoginStages.AuthenticatorOTPForm.authenticatorOTP.input.label')"
        :placeholder="t('sso.pages.LoginStages.AuthenticatorOTPForm.authenticatorOTP.input.placeholder')" 

      />
      <template #control="{ validate }">
        <DpButton
          purpose="primaryAction"
          size="large"
          @click="validate">
          {{ t("sso.pages.LoginStages.AuthenticatorOTPForm.confirmCode.buttonLabel") }}
        </DpButton>
      </template>
    </DpForm>
  </div>
</template>

<i18n>
{
  "ar-AE": {
    "unlockWith": {
      "label": "أدخل وأكد كود التوثيق من تطبيق التوثيق."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق",
        "label": "كود التوثيق من تطبيق التوثيق"
      }
    },
    "input": {
      "label": "كود التوثيق من تطبيق التوثيق"
    },
    "authenticatorOTPForm": {
      "title": "تطبيق المصادقة"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    }
  },
  "ar-DZ": {
    "unlockWith": {
      "label": "أدخل وأكد كود التصديق من تطبيق التصديق."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق",
        "label": "كود التوثيق من تطبيق التوثيق"
      }
    },
    "input": {
      "label": "كود التوثيق من تطبيق التوثيق"
    },
    "authenticatorOTPForm": {
      "title": "تطبيق المصادقة"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    }
  },
  "ar-EG": {
    "unlockWith": {
      "label": "أدخل و أكد كود التوثيق من تطبيق التوثيق."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق",
        "label": "كود التوثيق من تطبيق التوثيق"
      }
    },
    "input": {
      "label": "كود التوثيق من تطبيق التوثيق"
    },
    "authenticatorOTPForm": {
      "title": "تطبيق المصادقة"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    }
  },
  "ar-LB": {
    "unlockWith": {
      "label": "أدخل وأكد رمز التفويض من تطبيق التفويض."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق",
        "label": "كود التوثيق من تطبيق التوثيق"
      }
    },
    "input": {
      "label": "كود التوثيق من تطبيق التوثيق"
    },
    "authenticatorOTPForm": {
      "title": "تطبيق التوثيق"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    }
  },
  "ar-MA": {
    "unlockWith": {
      "label": "أدخل وأكد رمز التوثيق من تطبيق التوثيق."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "ادخل رمز التأكيد",
        "label": "كود التوثيق من تطبيق التوثيق"
      }
    },
    "input": {
      "label": "كود التوثيق من تطبيق التوثيق"
    },
    "authenticatorOTPForm": {
      "title": "تطبيق التوثيق"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    }
  },
  "ar-SA": {
    "unlockWith": {
      "label": "أدخل وتأكد من كود التصديق من تطبيق التصديق."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق",
        "label": "كود التوثيق من تطبيق التوثيق"
      }
    },
    "input": {
      "label": "كود التوثيق من تطبيق التوثيق"
    },
    "authenticatorOTPForm": {
      "title": "تطبيق المصادقة"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    }
  },
  "ar-SY": {
    "unlockWith": {
      "label": "أدخل و أكد الرمز من تطبيق التوثيق."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق",
        "label": "كود التوثيق من تطبيق التوثيق"
      }
    },
    "input": {
      "label": "كود التوثيق من تطبيق التوثيق"
    },
    "authenticatorOTPForm": {
      "title": "تطبيق المصادقة"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    }
  },
  "ar-TN": {
    "unlockWith": {
      "label": "أدخل واكد الرمز من تطبيق التوثيق."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق",
        "label": "كود التوثيق من تطبيق التوثيق"
      }
    },
    "input": {
      "label": "كود التوثيق من تطبيق التوثيق"
    },
    "authenticatorOTPForm": {
      "title": "تطبيق التوثيق"
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    }
  },
  "bg-BG": {
    "unlockWith": {
      "label": "Въведете и потвърдете кода за удостоверяване от приложението за удостоверяване."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Въведете код за удостоверяване",
        "label": "Код за удостоверяване от приложението за удостоверяване"
      }
    },
    "input": {
      "label": "Код за удостоверяване от приложението за удостоверяване"
    },
    "authenticatorOTPForm": {
      "title": "Приложение за удостоверяване"
    },
    "confirmCode": {
      "buttonLabel": "Потвърждение"
    }
  },
  "bs-BA": {
    "unlockWith": {
      "label": "Unesite i potvrdite autentifikacijski kod iz autentifikacijske aplikacije."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Unesite kod za autentifikaciju",
        "label": "Autentifikacijski kod iz autentifikacijske aplikacije"
      }
    },
    "input": {
      "label": "Autentifikacijski kod iz autentifikacijske aplikacije"
    },
    "authenticatorOTPForm": {
      "title": "Aplikacija za autentifikaciju"
    },
    "confirmCode": {
      "buttonLabel": "Potvrdite"
    }
  },
  "cs-CZ": {
    "unlockWith": {
      "label": "Zadejte a potvrďte ověřovací kód z ověřovací aplikace."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Zadejte ověřovací kód",
        "label": "Autentizační kód z autentizační aplikace"
      }
    },
    "input": {
      "label": "Autentizační kód z autentizační aplikace"
    },
    "authenticatorOTPForm": {
      "title": "Autentizační aplikace"
    },
    "confirmCode": {
      "buttonLabel": "Potvrdit"
    }
  },
  "da-DK": {
    "unlockWith": {
      "label": "Indtast og bekræft godkendelseskoden fra godkendelsesappen."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Indtast godkendelseskode",
        "label": "Autentificeringskode fra autentificeringsappen"
      }
    },
    "input": {
      "label": "Autentificeringskode fra autentificeringsappen"
    },
    "authenticatorOTPForm": {
      "title": "Autentificeringsapp"
    },
    "confirmCode": {
      "buttonLabel": "Bekræft"
    }
  },
  "de-DE": {
    "unlockWith": {
      "label": "Authentifizierungscode aus der Authentifizierungs-App eingeben und bestätigen."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Authentifizierungscode eingeben",
        "label": "Authentifizierungscode aus der Authentifizierungs-App"
      }
    },
    "input": {
      "label": "Authentifizierungs-Code aus der Authentifizierungs-App"
    },
    "authenticatorOTPForm": {
      "title": "Authentifizierungs-App"
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    }
  },
  "de-AT": {
    "unlockWith": {
      "label": "Authentifizierungscode aus der Authentifizierungs-App eingeben und bestätigen."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Authentifizierungscode eingeben",
        "label": "Authentifizierungscode aus der Authentifizierungs-App"
      }
    },
    "input": {
      "label": "Authentifizierungs-Code aus der Authentifizierungs-App"
    },
    "authenticatorOTPForm": {
      "title": "Authentifizierungs-App"
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    }
  },
  "de-CH": {
    "unlockWith": {
      "label": "Authentifizierungscode aus der Authentifizierungs-App eingeben und bestätigen."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Authentifizierungscode eingeben",
        "label": "Authentifizierungscode aus der Authentifizierungs-App"
      }
    },
    "input": {
      "label": "Authentifizierungs-Code aus der Authentifizierungs-App"
    },
    "authenticatorOTPForm": {
      "title": "Authentifizierungs-App"
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    }
  },
  "de-LU": {
    "unlockWith": {
      "label": "Authentifizierungscode aus der Authentifizierungs-App eingeben und bestätigen."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Authentifizierungscode eingeben",
        "label": "Authentifizierungscode aus der Authentifizierungs-App"
      }
    },
    "input": {
      "label": "Authentifizierungs-Code aus der Authentifizierungs-App"
    },
    "authenticatorOTPForm": {
      "title": "Authentifizierungs-App"
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    }
  },
  "el-GR": {
    "unlockWith": {
      "label": "Εισάγετε και επιβεβαιώστε τον κωδικό πιστοποίησης από την εφαρμογή πιστοποίησης."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Εισάγετε τον κωδικό επαλήθευσης",
        "label": "Κωδικός επικύρωσης από την εφαρμογή επικύρωσης"
      }
    },
    "input": {
      "label": "Κωδικός επικύρωσης από την εφαρμογή επικύρωσης"
    },
    "authenticatorOTPForm": {
      "title": "Εφαρμογή Επαλήθευσης"
    },
    "confirmCode": {
      "buttonLabel": "Επιβεβαίωση"
    }
  },
  "el-CY": {
    "unlockWith": {
      "label": "Εισάγετε και επιβεβαιώστε τον κωδικό επαλήθευσης από την εφαρμογή επαλήθευσης."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Εισάγετε τον κωδικό επαλήθευσης",
        "label": "Κωδικός επικύρωσης από την εφαρμογή επικύρωσης"
      }
    },
    "input": {
      "label": "Κωδικός επικύρωσης από την εφαρμογή επικύρωσης"
    },
    "authenticatorOTPForm": {
      "title": "Εφαρμογή Επαλήθευσης"
    },
    "confirmCode": {
      "buttonLabel": "Επιβεβαίωση"
    }
  },
  "en-GB": {
    "unlockWith": {
      "label": "Enter and confirm the authentication code from the authentication app."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Enter authentication code",
        "label": "Authentication Code from Authentication App"
      }
    },
    "input": {
      "label": "Authentication Code from Authentication App"
    },
    "authenticatorOTPForm": {
      "title": "Authentication App"
    },
    "confirmCode": {
      "buttonLabel": "Confirm"
    }
  },
  "en-IE": {
    "unlockWith": {
      "label": "Enter and confirm the authentication code from the authentication app."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Enter authentication code",
        "label": "Authentication Code from Authentication App"
      }
    },
    "input": {
      "label": "Authentication Code from Authentication App"
    },
    "authenticatorOTPForm": {
      "title": "Authentication App"
    },
    "confirmCode": {
      "buttonLabel": "Confirm"
    }
  },
  "en-US": {
    "unlockWith": {
      "label": "Enter and confirm the authentication code from the authentication app."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Enter authentication code",
        "label": "Authentication Code from Authentication App"
      }
    },
    "input": {
      "label": "Authentication Code from Authentication App"
    },
    "authenticatorOTPForm": {
      "title": "Authentication App"
    },
    "confirmCode": {
      "buttonLabel": "Confirm"
    }
  },
  "es-ES": {
    "unlockWith": {
      "label": "Introduce y confirma el código de autenticación de la aplicación de autenticación."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Ingrese el código de autenticación",
        "label": "Código de autenticación de la aplicación de autenticación"
      }
    },
    "input": {
      "label": "Código de autenticación de la aplicación de autenticación"
    },
    "authenticatorOTPForm": {
      "title": "Aplicación de autenticación"
    },
    "confirmCode": {
      "buttonLabel": "Confirmar"
    }
  },
  "et-EE": {
    "unlockWith": {
      "label": "Sisestage ja kinnitage autentimisrakenduse autentimiskood."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Sisestage autentimiskood",
        "label": "Autentimiskood autentimisrakendusest"
      }
    },
    "input": {
      "label": "Autentimiskood autentimisrakendusest"
    },
    "authenticatorOTPForm": {
      "title": "Autentimisrakendus"
    },
    "confirmCode": {
      "buttonLabel": "Kinnita"
    }
  },
  "fi-FI": {
    "unlockWith": {
      "label": "Anna ja vahvista tunnistussovelluksen tunnistuskoodi."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Kirjoita tunnistekoodi",
        "label": "Autentikointikoodi autentikointisovelluksesta"
      }
    },
    "input": {
      "label": "Autentikointikoodi autentikointisovelluksesta"
    },
    "authenticatorOTPForm": {
      "title": "Todennussovellus"
    },
    "confirmCode": {
      "buttonLabel": "Vahvista"
    }
  },
  "fr-BE": {
    "unlockWith": {
      "label": "Entrez et confirmez le code d'authentification de l'application d'authentification."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Entrez le code d'authentification",
        "label": "Code d'authentification de l'application d'authentification"
      }
    },
    "input": {
      "label": "Code d'authentification de l'application d'authentification"
    },
    "authenticatorOTPForm": {
      "title": "Application d'authentification"
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    }
  },
  "fr-CH": {
    "unlockWith": {
      "label": "Saisissez et confirmez le code d'authentification de l'application d'authentification."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Saisir le code d'authentification",
        "label": "Code d'authentification de l'application d'authentification"
      }
    },
    "input": {
      "label": "Code d'authentification de l'application d'authentification"
    },
    "authenticatorOTPForm": {
      "title": "Application d'authentification"
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    }
  },
  "fr-FR": {
    "unlockWith": {
      "label": "Saisissez et confirmez le code d'authentification de l'application d'authentification."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Saisir le code d'authentification",
        "label": "Code d'authentification de l'application d'authentification"
      }
    },
    "input": {
      "label": "Code d'authentification de l'application d'authentification"
    },
    "authenticatorOTPForm": {
      "title": "Application d'authentification"
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    }
  },
  "fr-LU": {
    "unlockWith": {
      "label": "Entrez et confirmez le code d'authentification de l'application d'authentification."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Saisir le code d'authentification",
        "label": "Code d'authentification de l'application d'authentification"
      }
    },
    "input": {
      "label": "Code d'authentification de l'application d'authentification"
    },
    "authenticatorOTPForm": {
      "title": "Application d'authentification"
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    }
  },
  "ga-IE": {
    "unlockWith": {
      "label": "Cuir isteach agus deimhnigh an cód fíordheimhnithe ón aip fíordheimhnithe."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Cuir isteach cód fíordheimhnithe",
        "label": "Cód Fíordheimhnithe ón gCleas Fíordheimhnithe"
      }
    },
    "input": {
      "label": "Cód Fíordheimhnithe ón gCleas Fíordheimhnithe"
    },
    "authenticatorOTPForm": {
      "title": "Aip Authindála"
    },
    "confirmCode": {
      "buttonLabel": "Deimhnigh"
    }
  },
  "he-IL": {
    "unlockWith": {
      "label": "הזן ואשר את קוד האימות מאפליקציית האימות."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "הזן קוד אימות",
        "label": "קוד אימות מאפליקציית האימות"
      }
    },
    "input": {
      "label": "קוד אימות מאפליקציית האימות"
    },
    "authenticatorOTPForm": {
      "title": "אפליקציית אימות"
    },
    "confirmCode": {
      "buttonLabel": "לְאַשֵׁר"
    }
  },
  "hi-IN": {
    "unlockWith": {
      "label": "प्रमाणन ऐप से प्रमाणन कोड दर्ज करें और पुष्टि करें."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "प्रमाणीकरण कोड दर्ज करें",
        "label": "प्रमाणीकरण ऐप से प्रमाणीकरण कोड"
      }
    },
    "input": {
      "label": "प्रमाणीकरण ऐप से प्रमाणीकरण कोड"
    },
    "authenticatorOTPForm": {
      "title": "प्रमाणीकरण ऐप"
    },
    "confirmCode": {
      "buttonLabel": "पुष्टि करें"
    }
  },
  "hr-HR": {
    "unlockWith": {
      "label": "Unesite i potvrdite autentikacijski kod iz autentikacijske aplikacije."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Unesite autentifikacijski kod",
        "label": "Autentifikacijski kod iz autentifikacijske aplikacije"
      }
    },
    "input": {
      "label": "Autentifikacijski kod iz autentifikacijske aplikacije"
    },
    "authenticatorOTPForm": {
      "title": "Aplikacija za autentifikaciju"
    },
    "confirmCode": {
      "buttonLabel": "Potvrda"
    }
  },
  "hu-HU": {
    "unlockWith": {
      "label": "Adja meg és erősítse meg az hitelesítő appból származó hitelesítő kódot."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Adja meg az hitelesítő kódot",
        "label": "Hitelesítő kód a hitelesítő alkalmazásból"
      }
    },
    "input": {
      "label": "Hitelesítő kód a hitelesítő alkalmazásból"
    },
    "authenticatorOTPForm": {
      "title": "Hitelesítő alkalmazás"
    },
    "confirmCode": {
      "buttonLabel": "Megerősít"
    }
  },
  "it-CH": {
    "unlockWith": {
      "label": "Inserire e confermare il codice di autenticazione dall'app di autenticazione."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Inserire il codice di autenticazione",
        "label": "Codice di autenticazione dall'app di autenticazione"
      }
    },
    "input": {
      "label": "Codice di autenticazione dall'app di autenticazione"
    },
    "authenticatorOTPForm": {
      "title": "App di autenticazione"
    },
    "confirmCode": {
      "buttonLabel": "Confermare"
    }
  },
  "it-IT": {
    "unlockWith": {
      "label": "Inserire e confermare il codice di autenticazione dell'app di autenticazione."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Inserire il codice di autenticazione",
        "label": "Codice di autenticazione dall'app di autenticazione"
      }
    },
    "input": {
      "label": "Codice di autenticazione dall'app di autenticazione"
    },
    "authenticatorOTPForm": {
      "title": "App di autenticazione"
    },
    "confirmCode": {
      "buttonLabel": "Conferma"
    }
  },
  "ja-JP": {
    "unlockWith": {
      "label": "認証アプリからの認証コードを入力して確認します。"
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "認証コードを入力してください",
        "label": "認証アプリからの認証コード"
      }
    },
    "input": {
      "label": "認証アプリからの認証コード"
    },
    "authenticatorOTPForm": {
      "title": "認証アプリ"
    },
    "confirmCode": {
      "buttonLabel": "確認"
    }
  },
  "ko-KR": {
    "unlockWith": {
      "label": "인증 앱에서 인증 코드를 입력하고 확인하십시오."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "인증 코드를 입력하세요",
        "label": "인증 앱에서의 인증 코드"
      }
    },
    "input": {
      "label": "인증 앱에서의 인증 코드"
    },
    "authenticatorOTPForm": {
      "title": "인증 앱"
    },
    "confirmCode": {
      "buttonLabel": "확인"
    }
  },
  "lt-LT": {
    "unlockWith": {
      "label": "Įveskite ir patvirtinkite autentifikavimo kodą iš autentifikavimo programos."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Įveskite autentifikacijos kodą",
        "label": "Autentifikacijos kodas iš autentifikacijos programos"
      }
    },
    "input": {
      "label": "Autentifikacijos kodas iš autentifikacijos programos"
    },
    "authenticatorOTPForm": {
      "title": "Autentifikavimo programa"
    },
    "confirmCode": {
      "buttonLabel": "Patvirtinti"
    }
  },
  "lv-LV": {
    "unlockWith": {
      "label": "Ievadiet un apstipriniet autentifikācijas kodu no autentifikācijas lietotnes."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Ievadiet autentifikācijas kodu",
        "label": "Autentifikācijas kods no autentifikācijas lietotnes"
      }
    },
    "input": {
      "label": "Autentifikācijas kods no autentifikācijas lietotnes"
    },
    "authenticatorOTPForm": {
      "title": "Autentifikācijas lietotne"
    },
    "confirmCode": {
      "buttonLabel": "Apstiprināt"
    }
  },
  "mt-MT": {
    "unlockWith": {
      "label": "Idħol u iċċekkja l-kodiċi tal-aġentazzjoni mill-app tal-aġentazzjoni."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Idħol il-kodiċi ta' awtentikazzjoni",
        "label": "Kodiċi ta' awtentiċazzjoni mill-app ta' awtentiċazzjoni"
      }
    },
    "input": {
      "label": "Kodiċi ta' awtentiċazzjoni mill-app ta' awtentiċazzjoni"
    },
    "authenticatorOTPForm": {
      "title": "App ta' awtentikazzjoni"
    },
    "confirmCode": {
      "buttonLabel": "Ikkonferma"
    }
  },
  "nb-NO": {
    "unlockWith": {
      "label": "Skriv inn og bekreft godkjenningskoden fra godkjenningsappen."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Skriv inn godkjenningskode",
        "label": "Autentiseringskode fra autentiseringsappen"
      }
    },
    "input": {
      "label": "Autentiseringskode fra autentiseringsappen"
    },
    "authenticatorOTPForm": {
      "title": "Autentiseringsapp"
    },
    "confirmCode": {
      "buttonLabel": "Bekrefte"
    }
  },
  "nl-BE": {
    "unlockWith": {
      "label": "Voer de verificatiecode in van de verificatie-app en bevestig deze."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Voer de authenticatiecode in",
        "label": "Authenticatiecode uit de authenticatie-app"
      }
    },
    "input": {
      "label": "Authenticatiecode uit de authenticatie-app"
    },
    "authenticatorOTPForm": {
      "title": "Authenticatie-app"
    },
    "confirmCode": {
      "buttonLabel": "Bevestigen"
    }
  },
  "nl-NL": {
    "unlockWith": {
      "label": "Voer de verificatiecode in van de verificatie-app en bevestig deze."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Voer de authenticatiecode in",
        "label": "Authenticatiecode uit de authenticatie-app"
      }
    },
    "input": {
      "label": "Authenticatiecode uit de authenticatie-app"
    },
    "authenticatorOTPForm": {
      "title": "Authenticatie-app"
    },
    "confirmCode": {
      "buttonLabel": "Bevestigen"
    }
  },
  "pl-PL": {
    "unlockWith": {
      "label": "Wprowadź i potwierdź kod uwierzytelniania z aplikacji uwierzytelniania."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Wprowadź kod uwierzytelniania",
        "label": "Kod uwierzytelniania z aplikacji uwierzytelniania"
      }
    },
    "input": {
      "label": "Kod uwierzytelniania z aplikacji uwierzytelniania"
    },
    "authenticatorOTPForm": {
      "title": "Aplikacja do uwierzytelniania"
    },
    "confirmCode": {
      "buttonLabel": "Potwierdź"
    }
  },
  "pt-PT": {
    "unlockWith": {
      "label": "Introduza e confirme o código de autenticação da aplicação de autenticação."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Insira o código de autenticação",
        "label": "Código de autenticação do aplicativo de autenticação"
      }
    },
    "input": {
      "label": "Código de autenticação do aplicativo de autenticação"
    },
    "authenticatorOTPForm": {
      "title": "Aplicativo de autenticação"
    },
    "confirmCode": {
      "buttonLabel": "Confirmar"
    }
  },
  "ro-RO": {
    "unlockWith": {
      "label": "Introduceți și confirmați codul de autentificare din aplicația de autentificare."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Introduceți codul de autentificare",
        "label": "Codul de autentificare din aplicația de autentificare"
      }
    },
    "input": {
      "label": "Codul de autentificare din aplicația de autentificare"
    },
    "authenticatorOTPForm": {
      "title": "Aplicație de autentificare"
    },
    "confirmCode": {
      "buttonLabel": "Confirmare"
    }
  },
  "ru-RU": {
    "unlockWith": {
      "label": "Введите и подтвердите код аутентификации из приложения аутентификации."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Введите код аутентификации",
        "label": "Код аутентификации из приложения аутентификации"
      }
    },
    "input": {
      "label": "Код аутентификации из приложения аутентификации"
    },
    "authenticatorOTPForm": {
      "title": "Приложение для аутентификации"
    },
    "confirmCode": {
      "buttonLabel": "Подтвердить"
    }
  },
  "sk-SK": {
    "unlockWith": {
      "label": "Zadajte a potvrďte overovací kód z overovacej aplikácie."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Zadajte overovací kód",
        "label": "Autentizačný kód z autentizačnej aplikácie"
      }
    },
    "input": {
      "label": "Autentizačný kód z autentizačnej aplikácie"
    },
    "authenticatorOTPForm": {
      "title": "Autentifikačná aplikácia"
    },
    "confirmCode": {
      "buttonLabel": "Potvrdiť"
    }
  },
  "sl-SI": {
    "unlockWith": {
      "label": "Vnesite in potrdite kodo za avtentikacijo iz aplikacije za avtentikacijo."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Vnesite kodo za preverjanje pristnosti",
        "label": "Avtentikacijska koda iz avtentikacijske aplikacije"
      }
    },
    "input": {
      "label": "Avtentikacijska koda iz avtentikacijske aplikacije"
    },
    "authenticatorOTPForm": {
      "title": "Aplikacija za avtentikacijo"
    },
    "confirmCode": {
      "buttonLabel": "Potrdi"
    }
  },
  "sv-SE": {
    "unlockWith": {
      "label": "Ange och bekräfta autentiseringskoden från autentiseringsappen."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Ange autentiseringskod",
        "label": "Autentiseringskod från autentiseringsappen"
      }
    },
    "input": {
      "label": "Autentiseringskod från autentiseringsappen"
    },
    "authenticatorOTPForm": {
      "title": "Autentiseringsapp"
    },
    "confirmCode": {
      "buttonLabel": "Bekräfta"
    }
  },
  "th-TH": {
    "unlockWith": {
      "label": "ป้อนและยืนยันรหัสการตรวจสอบจากแอปการตรวจสอบ."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "ป้อนรหัสยืนยัน",
        "label": "รหัสการรับรองจากแอปการรับรองความถูกต้อง"
      }
    },
    "input": {
      "label": "รหัสการรับรองจากแอปการรับรองความถูกต้อง"
    },
    "authenticatorOTPForm": {
      "title": "แอปพลิเคชันการรับรองความถูกต้อง"
    },
    "confirmCode": {
      "buttonLabel": "ยืนยัน"
    }
  },
  "tr-CY": {
    "unlockWith": {
      "label": "Doğrulama uygulamasından doğrulama kodunu girin ve onaylayın."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Doğrulama kodunu girin",
        "label": "Doğrulama Uygulamasından Doğrulama Kodu"
      }
    },
    "input": {
      "label": "Doğrulama Uygulamasından Doğrulama Kodu"
    },
    "authenticatorOTPForm": {
      "title": "Kimlik Doğrulama Uygulaması"
    },
    "confirmCode": {
      "buttonLabel": "Onayla"
    }
  },
  "tr-TR": {
    "unlockWith": {
      "label": "Doğrulama uygulamasından doğrulama kodunu girin ve onaylayın."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Doğrulama kodunu girin",
        "label": "Doğrulama Uygulamasından Doğrulama Kodu"
      }
    },
    "input": {
      "label": "Doğrulama Uygulamasından Doğrulama Kodu"
    },
    "authenticatorOTPForm": {
      "title": "Kimlik Doğrulama Uygulaması"
    },
    "confirmCode": {
      "buttonLabel": "Onayla"
    }
  },
  "uk-UA": {
    "unlockWith": {
      "label": "Введіть та підтвердіть код аутентифікації з додатка аутентифікації."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Введіть код автентифікації",
        "label": "Код автентифікації з додатка автентифікації"
      }
    },
    "input": {
      "label": "Код автентифікації з додатка автентифікації"
    },
    "authenticatorOTPForm": {
      "title": "Додаток для автентифікації"
    },
    "confirmCode": {
      "buttonLabel": "Підтвердити"
    }
  },
  "vi-VN": {
    "unlockWith": {
      "label": "Nhập và xác nhận mã xác thực từ ứng dụng xác thực."
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "Nhập mã xác thực",
        "label": "Mã xác thực từ ứng dụng xác thực"
      }
    },
    "input": {
      "label": "Mã xác thực từ ứng dụng xác thực"
    },
    "authenticatorOTPForm": {
      "title": "Ứng dụng Xác thực"
    },
    "confirmCode": {
      "buttonLabel": "Xác nhận"
    }
  },
  "zh-CN": {
    "unlockWith": {
      "label": "输入并确认来自认证应用的认证代码。"
    },
    "authenticatorOTP": {
      "input": {
        "placeholder": "请输入认证码",
        "label": "来自身份验证应用的身份验证代码"
      }
    },
    "input": {
      "label": "来自身份验证应用的身份验证代码"
    },
    "authenticatorOTPForm": {
      "title": "认证应用"
    },
    "confirmCode": {
      "buttonLabel": "确认"
    }
  }
}
</i18n>
